import { useCallback, useEffect, useState } from "react";
import { KeyRound, Trash2, UserPlus, Phone, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS, ROLE_HINTS } from "@/lib/roles";

const EMPTY = { username: "", name: "", password: "", role: "operatore", phone: "" };
const selCls = "h-10 rounded-md border border-[var(--border)] px-3 text-sm bg-white";

function RoleOptions() {
  return Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>);
}

export function UsersManager({ currentId }) {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(EMPTY);

  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", f);
      toast.success("Account creato");
      setF(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const editPhone = async (u) => {
    const phone = window.prompt(`Cellulare WhatsApp di ${u.name || u.username} (es. 3331234567)`, u.phone || "");
    if (phone === null) return;
    try { await api.put(`/users/${u.id}`, { name: u.name, phone }); toast.success("Numero aggiornato"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Nuova password per ${u.username} (min 10 caratteri)`);
    if (!pw) return;
    try { await api.put(`/users/${u.id}/password`, { password: pw }); toast.success("Password aggiornata"); } catch (err) { toast.error(formatApiError(err)); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}/role`, { role }); toast.success(`Ruolo aggiornato: ${ROLE_LABELS[role]}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const setZgh = async (u, on) => {
    try { await api.put(`/users/${u.id}/wh-access`, { wh_access: on ? ["zgh"] : [] }); toast.success(on ? "Accesso Gaming House concesso" : "Accesso Gaming House tolto"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Disattivare la verifica in due passaggi di ${u.username}? Dovrà riconfigurarla.`)) return;
    try { await api.delete(`/users/${u.id}/2fa`); toast.success("Verifica in due passaggi azzerata"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'account ${u.username}?`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  return (
    <section className="rounded-2xl bg-white border border-[var(--border)] p-5 sm:p-6 space-y-5" data-testid="users-manager">
      <h2 className="font-heading text-lg font-semibold">Personale e accessi</h2>
      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3">
        <Input required placeholder="ID di accesso" autoCapitalize="none" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} data-testid="new-user-username-input" />
        <Input placeholder="Nome e cognome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name-input" />
        <Input required minLength={10} placeholder="Password (min 10)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password-input" />
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={selCls} data-testid="new-user-role-select">
          <RoleOptions />
        </select>
        <p className="text-xs text-[var(--muted)] sm:col-span-2" data-testid="new-user-role-hint">{ROLE_HINTS[f.role]}</p>
        <Input type="tel" placeholder="Cellulare WhatsApp (facoltativo)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="sm:col-span-2" data-testid="new-user-phone-input" />
        <button className="btn-primary h-11 px-5 sm:col-span-2 sm:w-fit" data-testid="new-user-submit-button"><UserPlus className="h-5 w-5" /> Crea account</button>
      </form>
      <ul className="divide-y divide-[var(--border)]" data-testid="users-list">
        {users.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`user-row-${u.username}`}>
            <div className="min-w-0">
              <p className="font-medium truncate">{u.name || u.username}</p>
              <p className="text-xs text-[var(--muted)] truncate">{u.username}{u.totp_enabled ? " · 2 passaggi attiva" : ""}{u.phone ? ` · ${u.phone}` : ""}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {u.id !== currentId
                ? <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className={`${selCls} h-9`} data-testid={`role-select-${u.username}`}><RoleOptions /></select>
                : <span className="text-xs font-semibold px-2" data-testid={`role-label-${u.username}`}>{ROLE_LABELS[u.role]}</span>}
              {u.role !== "admin" && (
                <label className="flex items-center gap-1 text-xs px-2" title="Accesso al magazzino ZoolashGamingHouse">
                  <input type="checkbox" checked={!!u.wh_access?.includes("zgh")} onChange={(e) => setZgh(u, e.target.checked)} data-testid={`zgh-access-${u.username}`} /> Gaming House
                </label>
              )}
              {u.totp_enabled && u.id !== currentId && <button onClick={() => reset2fa(u)} className="btn-ghost" data-testid={`reset-2fa-${u.username}`} aria-label="Azzera verifica in due passaggi"><ShieldOff className="h-4 w-4" /></button>}
              <button onClick={() => editPhone(u)} className="btn-ghost" data-testid={`edit-phone-${u.username}`} aria-label="Numero WhatsApp"><Phone className="h-4 w-4" /></button>
              <button onClick={() => resetPw(u)} className="btn-ghost" data-testid={`reset-password-${u.username}`} aria-label="Cambia password"><KeyRound className="h-4 w-4" /></button>
              {u.id !== currentId && <button onClick={() => remove(u)} className="btn-ghost text-red-600" data-testid={`delete-user-${u.username}`} aria-label="Elimina"><Trash2 className="h-4 w-4" /></button>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
